import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/axios';
import { getPostLoginPath } from '../config/adminRoles';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('EzSignly_admin_token');
    if (token) {
      // Verify token and get user data
      verifyToken();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyToken = async () => {
    try {
      // Set the token in the Authorization header
      const token = localStorage.getItem('EzSignly_admin_token');
      if (!token) {
        throw new Error('No token found');
      }

      // Verify the token and get user data
      const response = await api.get('/admin/profile');
      if (response.data.admin) {
        setUser(response.data.admin);
        // Redirect to role-appropriate home (Support/SEO skip dashboard)
        if (window.location.pathname === '/login' || window.location.pathname === '/') {
          navigate(getPostLoginPath(response.data.admin.role));
        }
      } else {
        throw new Error('No user data received');
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/admin/login', {
        email,
        password
      });

      const { token, admin } = response.data;
      localStorage.setItem('EzSignly_admin_token', token);
      setUser(admin);
      return { admin };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to login');
    }
  };

  const forgotPassword = async (email) => {
    try {
      await api.post('/admin/forgot-password', { email });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to send reset password email');
    }
  };

  const resetPassword = async (email, otp, newPassword) => {
    try {
      await api.post('/admin/reset-password', {
        email,
        otp,
        newPassword
      });
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const logout = () => {
    localStorage.removeItem('EzSignly_admin_token');
    setUser(null);
    navigate('/login');
  };

  const value = {
    user,
    loading,
    login,
    forgotPassword,
    resetPassword,
    logout,
    verifyToken
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}; 